import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  IdentificationIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  subjectsService,
  Subject,
  SubjectCaseType,
} from '@/services/subjects.service';
import { getFullImageUrl } from '@/utils/imageUrl';

interface LinkedSubjectsPanelProps {
  caseId: string;
  caseType: SubjectCaseType;
  canManage?: boolean;
}

export const LinkedSubjectsPanel: React.FC<LinkedSubjectsPanelProps> = ({
  caseId,
  caseType,
  canManage = true,
}) => {
  const navigate = useNavigate();
  const [linked, setLinked] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const fetchLinked = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters =
        caseType === 'INVESTIGATION' ? { investigationId: caseId } : { incidentId: caseId };
      const result = await subjectsService.getSubjects({ ...filters, limit: 100 });
      setLinked(result.subjects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, [caseId, caseType]);

  useEffect(() => {
    fetchLinked();
  }, [fetchLinked]);

  const handleUnlink = async (subject: Subject) => {
    // eslint-disable-next-line no-alert
    if (!confirm(`Unlink ${subject.firstName} ${subject.lastName} from this ${caseType.toLowerCase()}?`)) {
      return;
    }
    try {
      await subjectsService.unlinkSubjectFromCase(subject.id, caseType, caseId);
      await fetchLinked();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err instanceof Error ? err.message : 'Failed to unlink');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Subjects ({linked.length})</CardTitle>
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => setShowLinkDialog(true)}>
              <PlusIcon className="w-4 h-4 mr-1" />
              Link Subject
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : error ? (
            <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
          ) : linked.length === 0 ? (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              No subjects linked to this {caseType.toLowerCase()}.
            </p>
          ) : (
            <ul className="space-y-2">
              {linked.map((subject) => {
                const roleConfig = subjectsService.getRoleTypeConfig(subject.roleType);
                const riskConfig = subjectsService.getRiskLevelConfig(subject.riskLevel);
                return (
                  <li
                    key={subject.id}
                    className="flex items-center gap-3 p-3 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/security/subjects/${subject.id}`)}
                      className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center"
                    >
                      {getFullImageUrl(subject.photoUrl) ? (
                        <img src={getFullImageUrl(subject.photoUrl) ?? undefined} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <IdentificationIcon className="w-5 h-5 text-secondary-400" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/security/subjects/${subject.id}`)}
                        className="text-sm font-medium text-secondary-900 dark:text-white hover:text-primary-600 truncate"
                      >
                        {subjectsService.getSubjectDisplayName(subject)}
                      </button>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${subjectsService.getBadgeClasses(roleConfig.color)}`}
                        >
                          {roleConfig.label}
                        </span>
                        {subject.riskLevel && (
                          <span
                            className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${subjectsService.getBadgeClasses(riskConfig.color)}`}
                          >
                            {riskConfig.label}
                          </span>
                        )}
                        {subject.email && (
                          <span className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                            · {subject.email}
                          </span>
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => handleUnlink(subject)}
                        className="p-1.5 text-secondary-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg"
                        aria-label="Unlink subject"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {showLinkDialog && (
        <LinkSubjectDialog
          caseId={caseId}
          caseType={caseType}
          existingSubjectIds={new Set(linked.map((s) => s.id))}
          onClose={() => setShowLinkDialog(false)}
          onLinked={() => {
            setShowLinkDialog(false);
            fetchLinked();
          }}
        />
      )}
    </>
  );
};

interface LinkSubjectDialogProps {
  caseId: string;
  caseType: SubjectCaseType;
  existingSubjectIds: Set<string>;
  onClose: () => void;
  onLinked: () => void;
}

const LinkSubjectDialog: React.FC<LinkSubjectDialogProps> = ({
  caseId,
  caseType,
  existingSubjectIds,
  onClose,
  onLinked,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Subject | null>(null);
  const [caseRole, setCaseRole] = useState('');
  const [relationshipToCase, setRelationshipToCase] = useState('');
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = setTimeout(async () => {
      try {
        setLoading(true);
        const result = await subjectsService.getSubjects({ search: search || undefined, limit: 20 });
        setResults(result.subjects.filter((s) => !existingSubjectIds.has(s.id)));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [search, existingSubjectIds]);

  const handleLink = async () => {
    if (!selected) { return; }
    setLinking(true);
    setError(null);
    try {
      await subjectsService.linkSubjectToCase(selected.id, {
        caseId,
        caseType,
        caseRole: caseRole || undefined,
        relationshipToCase: relationshipToCase || undefined,
      });
      onLinked();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link subject');
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-16" role="dialog">
      <div className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Link Subject
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-white"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {!selected ? (
            <>
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search subjects…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                  autoFocus
                />
              </div>

              {loading ? (
                <p className="text-sm text-secondary-500 text-center py-4">Searching…</p>
              ) : results.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">
                    No matching subjects found.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      onClose();
                      navigate('/security/subjects/create');
                    }}
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Create New Subject
                  </Button>
                </div>
              ) : (
                <ul className="space-y-1 max-h-64 overflow-y-auto">
                  {results.map((s) => {
                    const roleConfig = subjectsService.getRoleTypeConfig(s.roleType);
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(s)}
                          className="w-full flex items-center gap-3 p-2 text-left rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800"
                        >
                          <div className="w-8 h-8 rounded-full bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center overflow-hidden">
                            {getFullImageUrl(s.photoUrl) ? (
                              <img src={getFullImageUrl(s.photoUrl) ?? undefined} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <IdentificationIcon className="w-4 h-4 text-secondary-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                              {subjectsService.getSubjectDisplayName(s)}
                            </p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                              {roleConfig.label}
                              {s.email ? ` · ${s.email}` : ''}
                            </p>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 border border-secondary-200 dark:border-secondary-700 rounded-lg">
                <LinkIcon className="w-5 h-5 text-primary-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    {subjectsService.getSubjectDisplayName(selected)}
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    {subjectsService.getRoleTypeConfig(selected.roleType).label}
                  </p>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-xs text-primary-600 hover:underline"
                >
                  Change
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Role on this {caseType.toLowerCase()}
                </label>
                <input
                  type="text"
                  value={caseRole}
                  onChange={(e) => setCaseRole(e.target.value)}
                  placeholder="e.g., Primary suspect, Key witness"
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Relationship / notes
                </label>
                <textarea
                  rows={3}
                  value={relationshipToCase}
                  onChange={(e) => setRelationshipToCase(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                />
              </div>

              {error && <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-secondary-200 dark:border-secondary-700">
          <Button variant="outline" onClick={onClose} disabled={linking}>
            Cancel
          </Button>
          {selected && (
            <Button onClick={handleLink} disabled={linking}>
              {linking ? 'Linking…' : 'Link Subject'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkedSubjectsPanel;
